import { useCallback, useEffect, useState } from 'react';
import { useAuthStore } from '@/shared/auth/store';
import { readPref, subscribe, writePref } from './storage';

/**
 * React hook over a single user preference key. Returns {@code [value, setValue]}.
 * Auto-updates when the value changes in another tab (or same tab via the custom
 * {@code ph-pref-change} event). Setter is stable per (userId, key) and writes through to
 * localStorage immediately.
 *
 * <p>Until the user is authed there's no userId — the hook reads/writes nothing and returns
 * the fallback. Once login lands, the hook re-syncs.
 */
export function usePrefState<T>(
  key: string,
  fallback: T,
): [T, (v: T | ((prev: T) => T)) => void] {
  const userId = useAuthStore((s) => s.session?.userId ?? '');
  const [value, setLocal] = useState<T>(() => readPref<T>(userId, key, fallback));

  // Re-read if userId changes (sign-in / sign-out / different user).
  useEffect(() => {
    setLocal(readPref<T>(userId, key, fallback));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, key]);

  // Cross-tab + same-tab sync.
  useEffect(() => {
    if (!userId) return;
    return subscribe(userId, key, () => setLocal(readPref<T>(userId, key, fallback)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, key]);

  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      setLocal((prev) => {
        const resolved = typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
        if (userId) writePref(userId, key, resolved);
        return resolved;
      });
    },
    [userId, key],
  );

  return [value, setValue];
}
